import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { useLoadScript } from '@react-google-maps/api';
import '../../styles/googlePlacesAutocomplete.css';
import styles from './styles.module.css';

const libraries = ['places'];
const allowLegacyAutocomplete =
  import.meta.env.VITE_GOOGLE_ALLOW_LEGACY_AUTOCOMPLETE === 'true';
const forceLegacyAutocomplete =
  import.meta.env.VITE_GOOGLE_FORCE_LEGACY_AUTOCOMPLETE === 'true';

const parseAddressComponents = (components = []) => {
  if (!Array.isArray(components)) {
    return {};
  }

  const findComponent = (type) =>
    components.find((component) => component.types.includes(type));
  const getLongName = (component) =>
    component?.long_name ?? component?.longText ?? '';
  const getShortName = (component) =>
    component?.short_name ?? component?.shortText ?? '';

  const streetNumber = getLongName(findComponent('street_number')) || '';
  const route = getLongName(findComponent('route')) || '';
  const city =
    getLongName(findComponent('locality')) ||
    getLongName(findComponent('sublocality_level_1')) ||
    getLongName(findComponent('administrative_area_level_3')) ||
    '';
  const county =
    getLongName(findComponent('administrative_area_level_2')) || '';
  const stateComponent = findComponent('administrative_area_level_1');
  const postalCode = getLongName(findComponent('postal_code')) || '';
  const country = getLongName(findComponent('country')) || '';

  return {
    street: [streetNumber, route].filter(Boolean).join(' ').trim(),
    city: city || '',
    county: county || '',
    stateName: getLongName(stateComponent) || '',
    stateCode: getShortName(stateComponent) || '',
    postalCode,
    country,
  };
};

const GooglePlacesAddressInput = forwardRef(
  (
    {
      value,
      placeholder,
      disabled,
      onChange,
      onAddressSelected,
      name,
      id,
      isError = false,
      errorMessage,
      showTypingHint = false,
      inputClassName,
    },
    ref
  ) => {
    const internalRef = useRef(null);
    const inputRef = ref || internalRef;
    const autocompleteRef = useRef(null);
    const placeElementRef = useRef(null);
    const containerRef = useRef(null);
    const onChangeRef = useRef(onChange);
    const onAddressSelectedRef = useRef(onAddressSelected);
    const [usePlaceAutocomplete, setUsePlaceAutocomplete] = useState(false);

    const apiKey = import.meta.env.VITE_GOOGLE_PLACES_API_KEY;
    const [scriptHasAttempted, setScriptHasAttempted] = useState(false);

    const shouldLoadScript = useMemo(() => Boolean(apiKey), [apiKey]);

    const { isLoaded, loadError } = useLoadScript({
      googleMapsApiKey: apiKey || '',
      libraries,
      version: 'weekly',
      preventGoogleFontsLoading: true,
      language: 'en',
      id: 'google-places-script',
      nonce: undefined,
      onLoad: () => setScriptHasAttempted(true),
      onError: () => setScriptHasAttempted(true),
    });

    useEffect(() => {
      if (!shouldLoadScript) {
        setScriptHasAttempted(true);
      }
    }, [shouldLoadScript]);

    useEffect(() => {
      onChangeRef.current = onChange;
    }, [onChange]);

    useEffect(() => {
      onAddressSelectedRef.current = onAddressSelected;
    }, [onAddressSelected]);

    const normalizePlaceId = (rawId) => {
      if (!rawId || typeof rawId !== 'string') {
        return '';
      }
      return rawId.startsWith('places/') ? rawId.replace(/^places\//, '') : rawId;
    };

    const fetchPlaceDetailsById = async (rawPlaceId) => {
      const placeId = normalizePlaceId(rawPlaceId);
      if (!placeId) {
        return null;
      }

      if (!apiKey) {
        return null;
      }

      try {
        const response = await fetch(
          `https://places.googleapis.com/v1/places/${encodeURIComponent(
            placeId
          )}?fields=addressComponents,formattedAddress&key=${encodeURIComponent(
            apiKey
          )}`
        );
        if (!response.ok) {
          return null;
        }
        return await response.json();
      } catch (error) {
        return null;
      }
    };

    useEffect(() => {
      let cleanupFn;
      const loadPlaceAutocomplete = async () => {
        if (
          !shouldLoadScript ||
          !isLoaded ||
          loadError ||
          !containerRef.current ||
          placeElementRef.current ||
          forceLegacyAutocomplete ||
          allowLegacyAutocomplete
        ) {
          return;
        }

        let PlaceElementConstructor =
          window.google?.maps?.places?.PlaceAutocompleteElement;

        if (!PlaceElementConstructor && window.google?.maps?.importLibrary) {
          try {
            const placesLib = await window.google.maps.importLibrary('places');
            PlaceElementConstructor = placesLib?.PlaceAutocompleteElement;
          } catch (error) {
            // Ignore import errors and allow fallback to legacy Autocomplete.
          }
        }

        if (!PlaceElementConstructor) {
          return;
        }

        const element = new PlaceElementConstructor();
        element.className = styles.placeAutocomplete;
        if (id) {
          element.id = id;
        }
        if (name) {
          element.setAttribute('name', name);
        }
        if (placeholder) {
          element.placeholder = placeholder;
        }
        if (typeof element.disabled === 'boolean') {
          element.disabled = Boolean(disabled);
        }
        containerRef.current.innerHTML = '';
        containerRef.current.appendChild(element);
        placeElementRef.current = element;
        setUsePlaceAutocomplete(true);

        const ensurePlaceElementVisible = () => {
          if (!placeElementRef.current) {
            return;
          }
          const height = placeElementRef.current.offsetHeight;
          const hasShadow = Boolean(placeElementRef.current.shadowRoot);

          if (!hasShadow || height === 0) {
            containerRef.current.innerHTML = '';
            placeElementRef.current = null;
            setUsePlaceAutocomplete(false);
          }
        };

        const visibilityCheck = setTimeout(ensurePlaceElementVisible, 150);
        const visibilityCheckSecond = setTimeout(ensurePlaceElementVisible, 500);

        const handlePlaceChanged = async (event) => {
          const placeFromEvent = event?.place || event?.detail?.place || null;
          const place = placeFromEvent || (element.getPlace ? element.getPlace() : null);
          if (!place) {
            return;
          }

          const placeId =
            place.placeId || place.place_id || place.id || place.placeId;

          if (place.fetchFields) {
            try {
              await place.fetchFields({
                fields: ['addressComponents', 'formattedAddress'],
              });
            } catch (error) {
              // Ignore fetchFields errors and fall back to existing data.
            }
          }

          let components =
            place.addressComponents || place.address_components || [];
          let formattedAddress =
            place.formattedAddress ||
            place.formatted_address ||
            element.value ||
            '';

          if ((!components || components.length === 0) && placeId) {
            const details = await fetchPlaceDetailsById(placeId);
            if (details) {
              components =
                details.addressComponents || details.address_components || [];
              formattedAddress =
                details.formattedAddress ||
                details.formatted_address ||
                formattedAddress;
            }
          }

          const parsed = parseAddressComponents(components);
          const resolvedAddress =
            formattedAddress || parsed.street || element.value || '';

          if (onChangeRef.current) {
            onChangeRef.current(resolvedAddress);
          }

          if (onAddressSelectedRef.current) {
            console.log('🔍 [NEW API] ADDRESS DATA BEING SENT:', {
              formattedAddress: resolvedAddress,
              ...parsed,
            });
            onAddressSelectedRef.current({
              formattedAddress: resolvedAddress,
              ...parsed,
            });
          }
        };

        const handleInput = (event) => {
          if (onChangeRef.current) {
            onChangeRef.current(event.target.value);
          }
        };

        element.addEventListener('gmp-place-changed', handlePlaceChanged);
        element.addEventListener('gmp-placeselect', handlePlaceChanged);
        element.addEventListener('input', handleInput);

        return () => {
          clearTimeout(visibilityCheck);
          clearTimeout(visibilityCheckSecond);
          element.removeEventListener('gmp-place-changed', handlePlaceChanged);
          element.removeEventListener('gmp-placeselect', handlePlaceChanged);
          element.removeEventListener('input', handleInput);
        };
      };

      loadPlaceAutocomplete().then((cleanup) => {
        cleanupFn = cleanup;
      });

      return () => {
        if (cleanupFn) {
          cleanupFn();
        }
      };
    }, [
      isLoaded,
      loadError,
      shouldLoadScript,
      inputClassName,
      id,
      name,
      placeholder,
      disabled,
    ]);

    useEffect(() => {
      if (
        !shouldLoadScript ||
        !isLoaded ||
        loadError ||
        !inputRef.current ||
        autocompleteRef.current ||
        placeElementRef.current
      ) {
        return undefined;
      }

      if (!window.google?.maps?.places?.Autocomplete) {
        return undefined;
      }

      const autocomplete = new window.google.maps.places.Autocomplete(
        inputRef.current,
        {
          types: ['address'],
          componentRestrictions: { country: ['us', 'ca'] },
          fields: ['address_components', 'formatted_address'],
        }
      );

      autocompleteRef.current = autocomplete;

      const listener = autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (!place) {
          return;
        }

        const placeId =
          place.place_id || place.placeId || place.id || place.placeId;

        const applyPlaceData = async () => {
          let components =
            place.address_components || place.addressComponents || [];
          let formattedAddress =
            place.formatted_address ||
            place.formattedAddress ||
            inputRef.current.value;

          if ((!components || components.length === 0) && placeId) {
            const details = await fetchPlaceDetailsById(placeId);
            if (details) {
              components =
                details.addressComponents || details.address_components || [];
              formattedAddress =
                details.formattedAddress ||
                details.formatted_address ||
                formattedAddress;
            }
          }

          const parsed = parseAddressComponents(components);
          const resolvedAddress =
            formattedAddress || parsed.street || inputRef.current.value;

          if (onChangeRef.current) {
            onChangeRef.current(resolvedAddress);
          }

          if (onAddressSelectedRef.current) {
            onAddressSelectedRef.current({
              formattedAddress: resolvedAddress,
              ...parsed,
            });
          }
        };

        applyPlaceData();
      });

      return () => {
        if (listener) {
          window.google.maps.event.removeListener(listener);
        }
        autocompleteRef.current = null;
      };
    }, [
      isLoaded,
      loadError,
      shouldLoadScript,
      inputRef,
    ]);

    useEffect(() => {
      if (placeElementRef.current) {
        placeElementRef.current.value = value ?? '';
      }
    }, [value]);

    const handleChange = (event) => {
      if (onChangeRef.current) {
        onChangeRef.current(event.target.value);
      }
    };

    let helperText = '';
    if (!isError) {
      if (!shouldLoadScript && scriptHasAttempted) {
        helperText = 'Google Places not available. Enter address manually.';
      } else if (showTypingHint) {
        helperText = 'Start typing to search address';
      }
    }

    return (
      <div className="flex flex-col gap-1">
        <div
          ref={containerRef}
          style={{ display: usePlaceAutocomplete ? 'block' : 'none' }}
        />
        <input
          ref={inputRef}
          id={id}
          type="text"
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          name={name}
          disabled={disabled}
          style={{ display: usePlaceAutocomplete ? 'none' : 'block' }}
          className={
            inputClassName ||
            'px-2 py-2.5 rounded-lg border-2 border-primary disabled:bg-frameDisabled w-full text-black font-normal focus:outline-none focus:border-secondary focus:border-2 placeholder:p-xs-regular'
          }
        />
        {helperText && !isError && (
          <p className="text-xs text-neutral-500">{helperText}</p>
        )}
        {isError && errorMessage && (
          <p className="text-xs text-error-red">{errorMessage}</p>
        )}
      </div>
    );
  }
);

GooglePlacesAddressInput.displayName = 'GooglePlacesAddressInput';

export default GooglePlacesAddressInput;
